import React, {useState, useEffect} from 'react'
import {getGptResponse} from "../api/client";
import {useSelector} from "react-redux";
import {CenteredSpinner2} from "./CenteredSpinner";
import {Button, IconCheck, IconClose, IconDelete, IconEdit} from "@seismic/mantle";
import {getExperimentFromStorage, setExperimentInStorage} from "../helpers/experimentManager";


export const Result = ({prompt, model, temperature}) => {

    const selectedMeetingId = useSelector(state => state.users.selectedMeetingId);
    
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState(null);


    useEffect(() => {
        setBusy(true);
        setResponse(null);
        getGptResponse(selectedMeetingId, {
            prompt: prompt,
            model: model,
            temperature: temperature
        })
        .then(a => setResponse(a))
        .finally(() => setBusy(false));
    }, [selectedMeetingId])
    
    return <div style={{paddingTop: "8px"}}>
        {busy && <CenteredSpinner2 message="Generating..." />}
        {response && response.choices && response.choices.map((choice, index) => <div style={{whiteSpace: "pre-wrap"}} key={index}>{choice.message.content}</div>)}
    </div>
}


export const Experiment = ({experiment}) => {
    
    return <div style={{flex: 1}}>
        <h3 style={{paddingBottom: "12px", borderBottom: "1px solid #cfd4da"}}>{experiment.name}</h3>
        {experiment.type === "everything" && <div style={{paddingTop: "8px"}}>
            <Result prompt={experiment.prompt} model={experiment.model} temperature={experiment.temperature} />
        </div>}
        {experiment.type === "individual" && <>
        <h4 style={{paddingTop: "8px"}}>Summary</h4>
        <Result prompt={experiment.summaryPrompt} model={experiment.model} temperature={experiment.temperature}/>
        <h4>Key Points</h4>
        <Result prompt={experiment.keyPointsPrompt} model={experiment.model} temperature={experiment.temperature} />
        <h4>Action Items</h4>
        <Result prompt={experiment.actionItemsPrompt} model={experiment.model} temperature={experiment.temperature} />
        </>}
    </div>
}

export const CustomExperiment = () => {

    const [experiment, setExperiment] = useState(getExperimentFromStorage());
    const [editMode, setEditMode] = useState(false);
    const [prompt, setPrompt] = useState(experiment?.prompt ?? "");
    const [temperature, setTemperature] = useState(experiment?.temperature ?? 0);
    const models = ["gpt-35-turbo-16k", "gpt-4-32k"];
    const [selectedModel, setSelectedModel] = useState(experiment?.model ?? models[0]);
    const generate = () => {
        
        if(temperature < 0 || temperature > 1) {
            alert("Temperature must be between 0 and 1")
            return;
        }
        
        let exp = {
            "model": selectedModel,
            "temperature": temperature,
            "type": "everything",
            "prompt": prompt
        };
        
        setExperiment(exp);
        setExperimentInStorage(exp);
        setEditMode(false);
    }
    
    const clearExperiment = () => {
        setExperiment(null);
        setExperimentInStorage(null);
        setPrompt("");
        setTemperature(0);
        setSelectedModel(models[0]);
        setEditMode(false);
    }
    
    return <div style={{flex: 1}}>
        <div style={{paddingBottom: "8px", borderBottom: "1px solid #cfd4da"}}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <h3>Custom</h3>
                <div style={{display: "flex", gap: "8px"}}>
                    {editMode && <Button startAdornment={<IconCheck size={16} />} hideLabel={true} variant="ghost" label="Save" onClick={generate} />}
                    {editMode && <Button startAdornment={<IconDelete size={16} />} hideLabel={true} variant="ghost" label="Clear" onClick={clearExperiment} />}
                    <Button startAdornment={editMode ? <IconClose size={16} /> : <IconEdit size={16} />} hideLabel={true} variant="ghost" label={editMode ? "Cancel" : "Edit"} onClick={()=>setEditMode(!editMode)} />
                </div>
            </div>
        </div>
        {editMode && <>
        <div style={{display: "flex"}}>
            <div>
                <div>Model:</div>
                <select value={selectedModel} onChange={(e)=>setSelectedModel(e.target.value)}>
                    {models.map((model, index) => <option key={index} value={model}>{model}</option>)}
                </select>
            </div>
            <div>
                <div>Temperature:</div>
                <input type="number" min={0} max={1} step={.1} value={temperature} onChange={(e)=>setTemperature(e.target.value)} />
            </div>
        </div>
        <div>
            <div>Prompt:</div>
            <textarea style={{width: "100%", height: "60vh"}} value={prompt} onChange={(e)=>setPrompt(e.target.value)} />
        </div>
        </>}
        {!editMode && experiment && <Result prompt={experiment.prompt} model={experiment.model} temperature={experiment.temperature} />}
        {!editMode && !experiment && <div style={{paddingTop: "8px"}}><a style={{cursor: "pointer"}} onClick={()=>setEditMode(!editMode)}>Create your own experiment.</a></div>}
    </div>
}

export const MeetingSummaryTab = () => {
    
    const [experiments, setExperiments] = useState([{
        "name": "Tom (gpt-35-turbo-16k)",
        "model": "gpt-35-turbo-16k",
        "temperature": 0,
        "type": "everything",
        "prompt": "The user will provide you with a meeting transcript from a meeting between a seller and a prospect formatted in lines like below\nSpeaker: What the speaker said\n\nYour job is to:\ncreate a list of up to 5 of the most important action items that are detailed and business oriented and include the owner of the action items\nAnd also provide a summarized list of up to 8 key discussion points where you summarize important conversations and their speakers throughout the meeting (ignoring current meeting logistics, small talk, weather, vacation plans, sports, etc...)"
    },{
        "name": "Tom (gpt-4-32k)",
        "model": "gpt-4-32k",
        "temperature": 0,
        "type": "everything",
        "prompt": "The user will provide you with a meeting transcript from a meeting between a seller and a prospect formatted in lines like below\nSpeaker: What the speaker said\n\nYour job is to:\ncreate a list of up to 5 of the most important action items that are detailed and business oriented and include the owner of the action items\nAnd also provide a summarized list of up to 8 key discussion points where you summarize important conversations and their speakers throughout the meeting (ignoring current meeting logistics, small talk, weather, vacation plans, sports, etc...)"
    },{
        "name": "Open AI (gpt-35-turbo-16k)",
        "model": "gpt-35-turbo-16k",
        "temperature": 0,
        "type": "individual",
        "summaryPrompt": "You are a highly skilled AI trained in language comprehension and summarization. I would like you to read the following text and summarize it into a concise abstract paragraph. Aim to retain the most important points, providing a coherent and readable summary that could help a person understand the main points of the discussion without needing to read the entire text. Please avoid unnecessary details or tangential points.",
        "keyPointsPrompt": "You are a proficient AI with a specialty in distilling information into key points. Based on the following text, identify and list up to 5 main points that were discussed or brought up. These should be the most important ideas, findings, or topics that are crucial to the essence of the discussion. Your goal is to provide a list that someone could read to quickly understand what was talked about. Please list these action items clearly and concisely.",
        "actionItemsPrompt": "You are an AI expert in analyzing conversations and extracting action items. Please review the text and identify up to 5 tasks, assignments, or actions that were agreed upon or mentioned as needing to be done. These could be tasks assigned to specific individuals, or general actions that the group has decided to take. Please list these action items clearly and concisely."
    },{
        "name": "Open AI (gpt-4-32k)",
        "temperature": 0,
        "model": "gpt-4-32k",
        "type": "individual",
        "summaryPrompt": "You are a highly skilled AI trained in language comprehension and summarization. I would like you to read the following text and summarize it into a concise abstract paragraph. Aim to retain the most important points, providing a coherent and readable summary that could help a person understand the main points of the discussion without needing to read the entire text. Please avoid unnecessary details or tangential points.",
        "keyPointsPrompt": "You are a proficient AI with a specialty in distilling information into key points. Based on the following text, identify and list up to 5 main points that were discussed or brought up. These should be the most important ideas, findings, or topics that are crucial to the essence of the discussion. Your goal is to provide a list that someone could read to quickly understand what was talked about. Please list these action items clearly and concisely.",
        "actionItemsPrompt": "You are an AI expert in analyzing conversations and extracting action items. Please review the text and identify up to 5 tasks, assignments, or actions that were agreed upon or mentioned as needing to be done. These could be tasks assigned to specific individuals, or general actions that the group has decided to take. Please list these action items clearly and concisely."
    }])
    
    return <div style={{paddingTop: "8px", display: "flex", gap: "16px"}}>
        {experiments.map((experiment, index) => <Experiment key={index} experiment={experiment} />)}
        <CustomExperiment />
    </div>
}